import { Option, Result } from '@sapphire/result';

import {
  StandardSurveyPayloadInput,
  SurveyMarginFormState,
  SurveyPricingSnapshot,
} from '@ps/survey-utils';

import { SurveyAudienceForm } from '../../../utils';
import { InsightsChildState } from '../../insights/survey-creation-insights-state.model';
import {
  selectFeasibilityCpi,
  selectInsightsChildren,
  selectPricing,
} from '../../insights';
import { selectCoreChildren } from '../../core/survey-creation-core.selectors';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import { SurveyChildEntry } from '../../survey-creation-child-registry.helper';
import { DEFAULT_CORE_CHILD_STATE } from '../../survey-creation-state.constants';
import { selectAudienceChildren } from '../survey-creation-audience.selectors';
import { childPricing } from '../domain/child-pricing';
import { marginSnapshot } from '../domain/margin-snapshot';

export interface PreDraftChildDraft {
  readonly key: SurveyChildKey;
  readonly input: StandardSurveyPayloadInput;
}

export interface PreDraftChildDraftsInput {
  /** Keyed entries — the child state no longer carries its own key. */
  readonly children: readonly SurveyChildEntry[];
  /** Shared across rows: offer price, pause threshold and blend come from the one form. */
  readonly pricing: SurveyPricingSnapshot;
  /** Feasibility is estimated per row, so each row carries its own CPI. */
  readonly insightsChildren: Record<SurveyChildKey, InsightsChildState>;
  /**
   * The margin form on screen. It stands in for a row whose own form was never
   * opened, so every row is created with a margin — the backend only enables the
   * margin override on a survey whose create payload carries one.
   */
  readonly margin: SurveyMarginFormState;
  readonly userId: number;
  readonly companyId: number;
  /** Reported when the buyer has ticked fewer rows than the flow allows. */
  readonly tooFewSelectedMessage: string;
  /** Rows a flow needs before it can be saved — 2 for a tracker, 1 otherwise. */
  readonly minimumSelected?: number;
  /** Reported when a ticked row is invalid but carries no error of its own. */
  readonly invalidChildMessage: string;
}

interface PreDraftChildDraftsContextParams {
  readonly children: ReturnType<typeof selectAudienceChildren>;
  readonly coreChildren: ReturnType<typeof selectCoreChildren>;
  readonly pricingState: ReturnType<typeof selectPricing>;
  readonly feasibilityCpi: ReturnType<typeof selectFeasibilityCpi>;
  readonly insightsChildren: ReturnType<typeof selectInsightsChildren>;
  readonly margin: SurveyMarginFormState;
  readonly user: Option<{ id: number }>;
  readonly companyId: number;
  readonly noSignedInUserMessage: string;
  readonly tooFewSelectedMessage: string;
  readonly minimumSelected?: number;
  readonly invalidChildMessage: string;
}

export function buildPreDraftChildDraftsContext({
  children,
  coreChildren,
  pricingState,
  feasibilityCpi,
  insightsChildren,
  margin,
  user,
  companyId,
  noSignedInUserMessage,
  tooFewSelectedMessage,
  minimumSelected,
  invalidChildMessage,
}: PreDraftChildDraftsContextParams): Result<PreDraftChildDraftsInput, string> {
  return user.match({
    some: (signedInUser) =>
      Result.ok<PreDraftChildDraftsInput>({
        children: Object.entries(children).map(
          ([key, child]): SurveyChildEntry => [
            key,
            child,
            coreChildren[key] ?? DEFAULT_CORE_CHILD_STATE,
          ],
        ),
        pricing: {
          ...pricingState,
          feasibilityCpi,
        } satisfies SurveyPricingSnapshot,
        insightsChildren,
        margin,
        userId: signedInUser.id,
        companyId,
        tooFewSelectedMessage,
        minimumSelected,
        invalidChildMessage,
      }),
    none: () => Result.err(noSignedInUserMessage),
  });
}

export function buildPreDraftChildDrafts(
  input: PreDraftChildDraftsInput,
): Result<PreDraftChildDraft[], string> {
  const selected = input.children.filter(
    ([, , { participant }]) => participant,
  );

  if (selected.length < (input.minimumSelected ?? 1)) {
    return Result.err(input.tooFewSelectedMessage);
  }

  const drafts: PreDraftChildDraft[] = [];

  for (const entry of selected) {
    const draft = preDraftChildDraft(entry, input);

    if (draft.isErr()) {
      return Result.err(draft.unwrapErr());
    }

    drafts.push(draft.unwrap());
  }

  return Result.ok(drafts);
}

export function preDraftChildDraft(
  [key, child]: SurveyChildEntry,
  {
    pricing,
    insightsChildren,
    margin,
    userId,
    companyId,
    invalidChildMessage,
  }: PreDraftChildDraftsInput,
): Result<PreDraftChildDraft, string> {
  const form = SurveyAudienceForm.from({
    metadata: child.metadata,
    qualifications: child.qualifications,
    quotas: child.quotas,
  });

  return Option.from(form.data).match({
    some: ({ metadata, qualifications, quotas }) =>
      Result.ok<PreDraftChildDraft>({
        key,
        input: {
          metadata,
          pricing: childPricing(pricing, insightsChildren[key]),
          userId,
          companyId,
          targeting: {
            qualifications,
            quotas,
            locationFileUpload: child.locationFileUpload,
          },
          enableRespondentStatusFilter: false,
          ...marginSnapshot(child.margin)
            .orElse(() => marginSnapshot(margin))
            .mapOr({}, (snapshot) => ({ margin: snapshot })),
        } satisfies StandardSurveyPayloadInput,
      }),
    none: () =>
      Result.err(Option.from(form.error).unwrapOr(invalidChildMessage)),
  });
}
